import { useLazyQuery } from "@apollo/react-hooks";
import NotFoundPage from "@saleor/components/NotFoundPage";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useShop from "@saleor/hooks/useShop";
import { commonMessages } from "@saleor/intl";
import { SearchCornermarks_search_edges_node } from "@saleor/searches/types/SearchCornermarks";
import useCornermarkSearch from "@saleor/searches/useCornermarkSearch";
import { CornerMarkTypeEnum } from "@saleor/types/globalTypes";
import createMetadataCreateHandler from "@saleor/utils/handlers/metadataCreateHandler";
import {
  useMetadataUpdate,
  usePrivateMetadataUpdate
} from "@saleor/utils/metadata/updateMetadata";
import { useWarehouseList } from "@saleor/warehouses/queries";
import { warehouseAddPath } from "@saleor/warehouses/urls";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { decimal, weight } from "../../misc";
import ProductVariantCreatePage from "../components/ProductVariantCreatePage";
import { ProductVariantCreateData } from "../components/ProductVariantCreatePage/form";
import {
  useProductVariantReorderMutation,
  useVariantCreateMutation
} from "../mutations";
import {
  getErpProductVariant,
  useExtraChargeCategoryDataQuery,
  useProductVariantCreateQuery
} from "../queries";
import { productListUrl, productUrl, productVariantEditUrl } from "../urls";
import { createVariantReorderHandler } from "./ProductUpdate/handlers";

interface ProductVariantCreateProps {
  productId: string;
}

export const ProductVariant: React.FC<ProductVariantCreateProps> = ({
  productId
}) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const shop = useShop();
  const intl = useIntl();
  const {
    loadMore: loadMoreCornermarks,
    search: searchCornermark,
    result: searchCornermarkOpts
  } = useCornermarkSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      type: CornerMarkTypeEnum.NORMAL
    }
  });
  const warehouses = useWarehouseList({
    displayLoader: true,
    variables: {
      first: 50
    }
  });

  const { data, loading: productLoading } = useProductVariantCreateQuery({
    displayLoader: true,
    variables: { id: productId }
  });

  const [variantCreate, variantCreateResult] = useVariantCreateMutation({
    onCompleted: data => {
      if (data.productVariantCreate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        navigate(
          productVariantEditUrl(
            productId,
            data.productVariantCreate.productVariant.id
          )
        );
      }
    }
  });
  const [updateMetadata] = useMetadataUpdate({});
  const [updatePrivateMetadata] = usePrivateMetadataUpdate({});

  const product = data?.product;

  if (product === null) {
    return <NotFoundPage onBack={() => navigate(productListUrl())} />;
  }

  const [
    reorderProductVariants,
    reorderProductVariantsOpts
  ] = useProductVariantReorderMutation({});

  const handleVariantReorder = createVariantReorderHandler(product, variables =>
    reorderProductVariants({ variables })
  );
  const [extraChargeCategory, setExtraChargeCategory] = useState<string>("");
  const [newSku, setNewSku] = useState<string>("");
  const extraChargeCategoryData = useExtraChargeCategoryDataQuery({
    variables: {
      sku: newSku
    }
  });
  const handleBack = () => navigate(productUrl(productId));
  const handleCreate = async (formData: ProductVariantCreateData) => {
    const result = await variantCreate({
      variables: {
        input: {
          attributes: formData.attributes
            .filter(attribute => attribute.value !== "")
            .map(attribute => ({
              id: attribute.id,
              values: [attribute.value]
            })),
          costPrice: decimal(formData.costPrice),
          price: decimal(formData.price),
          cornerMark: ((formData.cornermark as undefined) as SearchCornermarks_search_edges_node)
            ? ((formData.cornermark as undefined) as SearchCornermarks_search_edges_node)
                .id
            : formData.cornermark,
          extrachargeCategoryName: extraChargeCategory,
          product: productId,
          sku: formData.sku,
          stocks: formData.stocks.map(stock => ({
            quantity: parseInt(stock.value, 0) || 0,
            warehouse: stock.id
          })),
          trackInventory: true,
          weight: weight(formData.weight)
        }
      }
    });

    return result.data.productVariantCreate?.productVariant?.id || null;
  };
  const handleSubmit = createMetadataCreateHandler(
    handleCreate,
    updateMetadata,
    updatePrivateMetadata
  );
  const handleVariantClick = (id: string) =>
    navigate(productVariantEditUrl(productId, id));

  const disableForm =
    productLoading ||
    variantCreateResult.loading ||
    reorderProductVariantsOpts.loading;

  const [erpProductVariant, getErpProductVariantQuery] = React.useState<any>(
    null
  );

  const [getErpProductVariantData] = useLazyQuery(getErpProductVariant, {
    fetchPolicy: "no-cache",
    onCompleted: data => {
      if (data?.erpProductVariant?.id) {
        getErpProductVariantQuery(data?.erpProductVariant);
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Sku was successfully loaded from erp.",
            description: "snackbar text"
          })
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Failed to load from erp.",
            description: "snackbar text"
          })
        });
      }
    }
  });

  function onGetErpSkuData(erpSku: string) {
    getErpProductVariantData({ variables: { sku: String(erpSku) } });
  }
  React.useEffect(() => {
    getErpProductVariantQuery(erpProductVariant);
  }, [erpProductVariant]);

  return (
    <>
      <WindowTitle
        title={intl.formatMessage({
          defaultMessage: "Create variant",
          description: "window title"
        })}
      />
      <ProductVariantCreatePage
        // 配镜方案下拉框
        extraChargeCategory={extraChargeCategory}
        setExtraChargeCategory={setExtraChargeCategory}
        setNewSku={setNewSku}
        extraChargeCategoryData={extraChargeCategoryData?.data}
        // end
        currencySymbol={shop?.defaultCurrency}
        cornermarks={(searchCornermarkOpts.data?.search.edges || []).map(
          edge => edge.node
        )}
        disabled={disableForm}
        errors={variantCreateResult.data?.productVariantCreate.errors || []}
        header={intl.formatMessage({
          defaultMessage: "Create Variant",
          description: "header"
        })}
        fetchCornermarks={searchCornermark}
        product={data?.product}
        onBack={handleBack}
        onSubmit={handleSubmit}
        onVariantClick={handleVariantClick}
        onWarehouseConfigure={() => navigate(warehouseAddPath)}
        onVariantReorder={handleVariantReorder}
        saveButtonBarState={variantCreateResult.status}
        fetchMoreCornermarks={{
          hasMore: searchCornermarkOpts.data?.search.pageInfo.hasNextPage,
          loading: searchCornermarkOpts.loading,
          onFetchMore: loadMoreCornermarks
        }}
        warehouses={
          warehouses.data?.warehouses.edges.map(edge => edge.node) || []
        }
        weightUnit={shop?.defaultWeightUnit}
        onGetErpData={onGetErpSkuData}
        erpProductVariant={erpProductVariant}
      />
    </>
  );
};
export default ProductVariant;
